import { useLocation, useParams } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import isEqual from 'lodash.isequal';
import { LockFormTypes, LockFormValues } from './types';
import { Pagination, PermissionEnum, Response } from '../../base-types';
import { ResponseError } from '../../resolver/types';
import responseErrorsResolver from '../../resolver/response-errors-resolver';
import LockForm from './components/lock-form';
import PermissionDenied from '../icons/permission-denied';
import SidePanelLoader from '../side-panel/loader';
import SidePanel from '../side-panel/side-panel';
import { SidePanelType } from '../side-panel/types';
import ButtonPrimary from '../button/button-primary';
import ButtonPseudo from '../button/button-pseudo';
import Tabs from '../tabs/tabs';
import Popup from '../popup/popup';
import SecurityLockLogs from './lock-item-logs';

import cn from './styles.module.css';

export default function SecurityLockForm({
  getLockItem,
  getLocks,
  create,
  update,
  getOrganizations,
  getLockItemUser,
  getUsers,
  lockItem,
  takeCount,
  lockItemUser,
  activePermissions,
  employees,
  clearLockPanelState,
  onClose,
  userId,
  isSuccess,
  organizationsIsLoading,
  organizationsIsFetching,
  usersIsLoading,
  usersIsFetching,
  isLoading,
  isFetching,
  requestInProgress,
  updateInProgress,
  withoutUserSelector,
  lockIdValue,
  formPanelVisible,
  onBlockUserClick,
  lockItemAuditLogsPagination,
  lockItemAuditLogs,
  getLockItemLogsList,
  lockItemLogsSchema,
  lockItemLogsFiltersSchema,
  lockItemLogsColumns,
  lockItemLogsColumnsLabels,
  lockItemLogsIsLoading,
  lockItemLogsIsFetching,
}: LockFormTypes) {
  const { lockId } = useParams();
  const { search } = useLocation();

  const lockIdString = lockIdValue || lockId;

  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [activeTab, setActiveTab] = useState<'general' | 'logs'>('general');
  const [globalError, setGlobalError] = useState('');
  const [searchText, setSearchText] = useState('');
  const [usersSearchText, setUsersSearchText] = useState('');
  const [organizations, setOrganizations] = useState<
    | {
        id: string;
        user: { id: string; profile: { firstName: string; lastName: string } };
      }[]
    | undefined
  >(undefined);
  const [organizationsPagination, setOrganizationsPagination] = useState<
    Pagination | undefined
  >(undefined);
  const [users, setUsers] = useState<
    { id: string; email?: string; phone: string }[] | undefined
  >(undefined);
  const [usersPagination, setUsersPagination] = useState<
    Pagination | undefined
  >(undefined);
  const [nextPageIsLoading, setNextPageIsLoading] = useState(false);
  const [nextUsersPageIsLoading, setNextUsersPageIsLoading] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
    setError,
    watch,
    setValue,
  } = useForm<LockFormValues>();

  const fields = watch();

  const isDirtyForm = !isEqual(
    {
      reason: fields.reason,
      expiresAt: fields.expiresAt,
      createdByComment: fields.createdByComment,
      locked: fields.locked,
    },
    {
      reason: lockItem?.reason,
      expiresAt: lockItem?.expiresAt,
      createdByComment: lockItem?.createdByComment,
      locked: lockItem?.locked,
    },
  );

  const isDirtyState = panelIsOpen && lockIdString ? isDirtyForm : isDirty;

  const getOrganizationsAction = useCallback(
    async ({
      cursor,
      searchValue,
    }: {
      cursor?: string;
      searchValue?: string;
    }) => {
      if (cursor) {
        setNextPageIsLoading(true);
      }

      if (getOrganizations) {
        const response = (await getOrganizations({
          cursor: cursor || '',
          search: searchValue || '',
        })) as {
          data: Response<{
            id: string;
            user: {
              id: string;
              profile: { firstName: string; lastName: string };
            };
          }>;
        };

        if (response && response.data) {
          let nextOrganizations;

          if (cursor) {
            setTimeout(() => {
              setNextPageIsLoading(false);
            }, 100);
          }

          if (!cursor) {
            nextOrganizations = response?.data?.data?.edges?.map(
              (item) => item.node,
            );
          } else if (cursor && organizations) {
            nextOrganizations = (
              [] as {
                id: string;
                user: {
                  id: string;
                  profile: { firstName: string; lastName: string };
                };
              }[]
            ).concat(organizations);

            response?.data?.data?.edges.forEach((item) =>
              nextOrganizations.push(item.node),
            );
          }

          setOrganizations(nextOrganizations);
          setOrganizationsPagination(response?.data?.data?.pageInfo);
        }
      }
    },
    [organizations],
  );

  const getUsersAction = useCallback(
    async ({ cursor, phone }: { cursor?: string; phone?: string }) => {
      if (cursor) {
        setNextUsersPageIsLoading(true);
      }

      if (getUsers) {
        const response = (await getUsers({
          cursor: cursor || '',
          phone: phone || '',
        })) as {
          data: Response<{ id: string; email?: string; phone: string }>;
        };

        if (response && response.data) {
          let nextUsers;

          if (cursor) {
            setTimeout(() => {
              setNextUsersPageIsLoading(false);
            }, 100);
          }

          if (!cursor) {
            nextUsers = response?.data?.data?.edges?.map((item) => item.node);
          } else if (cursor && users) {
            nextUsers = (
              [] as { id: string; email?: string; phone: string }[]
            ).concat(users);

            response?.data?.data?.edges.forEach((item) =>
              nextUsers.push(item.node),
            );
          }

          setUsers(nextUsers);
          setUsersPagination(response?.data?.data?.pageInfo);
        }
      }
    },
    [users],
  );

  const onSubmit = handleSubmit(async (values) => {
    let response;

    const currentDate = new Date().getTime();

    if (currentDate > new Date(values.expiresAt as string).getTime()) {
      setError('expiresAt', {
        type: 'custom',
        message: 'Expires At must be a future date',
      });
    } else {
      if (!lockIdString) {
        const body = { ...values, locked: true };

        delete body.userId;

        if (userId || values.userId) {
          body.userId = userId || values.userId;
        }

        response = (await create(body)) as { error?: ResponseError };
      } else {
        const body: LockFormValues = {
          reason: values.reason,
          expiresAt: values.expiresAt,
          createdByComment: values.createdByComment,
          locked: values.locked,
        };

        if (lockItem?.userId) {
          body.userId = lockItem.userId;
        }

        response = (await update({ id: lockIdString, body })) as {
          error?: ResponseError;
        };
      }

      if (response?.error) {
        responseErrorsResolver({
          errors: response.error,
          setError,
          setGlobalError,
        });
      }
    }
  });

  const handleClose = () => {
    clearLockPanelState();

    reset({ reason: '', expiresAt: '', createdByComment: '', locked: false });
    setGlobalError('');
    setPanelIsOpen(false);

    setOrganizations(undefined);
    setOrganizationsPagination(undefined);
    setSearchText('');
    setUsers(undefined);
    setUsersPagination(undefined);
    setUsersSearchText('');
    setDirtyPopupVisible(false);

    setTimeout(() => {
      onClose();
    }, 100);
  };

  useEffect(() => {
    if (lockIdString) {
      getLockItem(lockIdString);
    }
  }, [lockIdString]);

  useEffect(() => {
    if (lockItem) {
      reset({
        createdByComment: lockItem.createdByComment || '',
        expiresAt: lockItem.expiresAt || '',
        reason: lockItem.reason,
        userId: lockItem.userId,
        locked: lockItem.locked,
      });

      if (!userId && lockItem.userId && getLockItemUser) {
        getLockItemUser(lockItem.userId);
      }
    }
  }, [lockItem, userId]);

  useEffect(() => {
    if (lockItemUser) {
      setOrganizations([
        {
          id: lockItemUser.id,
          user: {
            id: lockItemUser.id,
            profile: {
              firstName: lockItemUser?.profile?.firstName || '',
              lastName: lockItemUser?.profile?.lastName || '',
            },
          },
        },
      ]);
    }
  }, [lockItemUser]);

  useEffect(() => {
    if (!onBlockUserClick) {
      setPanelIsOpen(true);
    }
  }, []);

  useEffect(() => {
    if (panelIsOpen && !organizations && !lockIdString && !userId) {
      getOrganizationsAction({});
    }
  }, [panelIsOpen, lockIdString, userId]);

  useEffect(() => {
    if (panelIsOpen && !users && !lockIdString && !userId) {
      getUsersAction({});
    }
  }, [panelIsOpen, lockIdString, userId]);

  useEffect(() => {
    if (isSuccess) {
      handleClose();

      getLocks(
        `?take=${takeCount}${userId ? `&userId=${userId}` : ''}${
          search ? search.replace('?', '&') : ''
        }`,
      );
    }
  }, [isSuccess]);

  let content = <div />;
  let buttonText;

  const lockForm = (
    <LockForm
      control={control}
      errors={errors}
      organizations={organizations?.map((item) => ({
        name: item?.user?.profile?.firstName
          ? `${item?.user?.profile?.firstName} ${item?.user?.profile?.lastName}`
          : '-',
        value: item.user.id,
      }))}
      organizationsPagination={organizationsPagination}
      nextPageIsLoading={nextPageIsLoading}
      getOrganizations={getOrganizationsAction}
      setSearchText={setSearchText}
      organizationsIsLoading={organizationsIsLoading || organizationsIsFetching}
      users={users?.map((item) => ({
        name: item.phone,
        suffix: item.email || '-',
        value: item.id,
      }))}
      usersPagination={usersPagination}
      nextUsersPageIsLoading={nextUsersPageIsLoading}
      getUsers={getUsersAction}
      setUsersSearchText={setUsersSearchText}
      usersIsLoading={usersIsLoading || usersIsFetching}
      isEditable={activePermissions?.includes(
        PermissionEnum.LOCKS_UPDATE_ACTION,
      )}
      userId={lockItem?.userId}
      lockId={lockIdString}
      searchText={searchText}
      usersSearchText={usersSearchText}
      setValue={setValue}
      employees={employees?.map((item) => ({
        name: `${item.profile?.firstName} ${item.profile?.lastName}`,
        value: item.id,
      }))}
      withoutUserSelector={withoutUserSelector}
    />
  );

  if (!lockIdString) {
    if (!activePermissions?.includes(PermissionEnum.LOCKS_CREATE_ACTION)) {
      content = <PermissionDenied />;
    } else {
      content = lockForm;
    }
  } else if (lockIdString) {
    if (isLoading || isFetching) {
      content = <SidePanelLoader />;
    } else {
      content = lockForm;
    }
  }

  if (requestInProgress || updateInProgress) {
    buttonText = 'Loading...';
  } else if (!lockIdString) {
    buttonText = 'Block user';
  } else {
    buttonText = 'Save';
  }

  return (
    <>
      <SidePanel
        closeOnClickOutside
        isOpen={formPanelVisible || panelIsOpen}
        onClose={isDirtyState ? () => setDirtyPopupVisible(true) : handleClose}
        title={lockIdString ? 'Lock details ' : 'Block a user'}
        errorSidepanel={globalError}
        type={SidePanelType.Transactions}
        sidepanelExpandedKey="lock-panel"
        footer={
          <>
            {((lockIdString &&
              activePermissions?.includes(
                PermissionEnum.LOCKS_UPDATE_ACTION,
              )) ||
              (!lockIdString &&
                activePermissions?.includes(
                  PermissionEnum.LOCKS_CREATE_ACTION,
                ))) && (
              <ButtonPrimary
                text={buttonText}
                onClick={onSubmit}
                disabled={
                  requestInProgress || updateInProgress || !isDirtyState
                }
                bold
              />
            )}

            <ButtonPseudo
              text="Cancel"
              onClick={
                isDirtyState ? () => setDirtyPopupVisible(true) : handleClose
              }
              bold
            />
          </>
        }
      >
        <>
          {lockIdString && (
            <div className={cn.tabsWrapper}>
              <Tabs
                tabs={[
                  { name: 'General Info', value: 'general' },

                  { name: 'Audit logs', value: 'logs' },
                ]}
                activeTab={activeTab}
                onClick={(tab) => setActiveTab(tab as 'general' | 'logs')}
              />
            </div>
          )}
          {activeTab === 'general' && content}
          {activeTab === 'logs' && (
            <SecurityLockLogs
              lockItemAuditLogs={lockItemAuditLogs}
              lockItemAuditLogsPagination={lockItemAuditLogsPagination}
              getList={getLockItemLogsList}
              schema={lockItemLogsSchema}
              filtersSchema={lockItemLogsFiltersSchema}
              activePermissions={activePermissions}
              columns={lockItemLogsColumns}
              columnsLabels={lockItemLogsColumnsLabels}
              isLoading={lockItemLogsIsLoading}
              isFetching={lockItemLogsIsFetching}
              lockId={lockIdString}
            />
          )}
        </>
      </SidePanel>
      <Popup
        title="You have unsaved changes that will be lost"
        description="Are you sure you want to close this form?"
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={handleClose}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <div id="dirty-popup" />
    </>
  );
}
